// @ts-strict-ignore
import capitalize from 'lodash/capitalize';
import type { InputActionMeta } from 'react-select';
import {
  DatePicker,
  Icon,
  IconName,
  MultiSelect,
  Tooltip,
  useSnackBar,
} from '@usealloy/component-library';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';

import { ActionType } from 'flow/store/engine/constants';
import type {
  Attribute,
} from 'flow/store/engine/types';
import {
  selectIsSearchBoxVisible,
  selectNodeFolderState,
  selectNodeLineageForAnchorNode,
  selectWorkflowData,
  selectWorkflowOperationDescs,
  selectWorkflowProps,
} from 'flow/store/redux/selectors';
import {
  dismissWorkflowOperationMessage,
  switchThresholdToDynamicComparison,
  switchThresholdToStaticComparison,
  updateActionRulesGrouping, updateActionRulesOperator, updateThreshold,
} from 'flow/store/redux/slice';
import {
  isActionOfType,
  getAllowedAttributesWithMatchingDataType,
} from 'flow/service/actions';

import { getAllowedAttributesForPortfolioWorkflow } from '~/flow/utils';
import { routes } from '~/helpers/routes';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import {
  canDynamicallyCompareActions,
  formatDateTime,
  getSelectProps,
  getSelectedOptionValue,
  getNonCyclicalActions,
  truncateLabel,
  computeRuleItemLengthLimit,
} from './helpers';
import { RuleEditorContext } from '../../NodeEditorDrawer/context';
import type { DropdownOption } from '../../NodeEditorDrawer/types';
import {
  CaretPositionSuffixTypes, MLAAttributeItemOrigin, RuleEditorDropdownMode,
  ThresholdRightAttributeOrigin,
} from '../../NodeEditorDrawer/types';
import { ThresholdContext } from '../context';
import { getOperatorsAsOptions } from '../operators';
import { DropdownContainer } from './styles';
import { CaretIndexType } from '../../useCaretControl/constants';
import { DEFAULT_INTEGER_MAX, DEFAULT_INTEGER_MIN } from '../constants';
import { onEscKeyFromRuleDropdown, getSelectedOption } from '../../helpers';
import { checkIfServiceHasMLAAttributes } from '~/dashboard/views/workflows/service/mla';
import { findLastOperation } from 'flow/service/assistance';
import { StyledCodeStyleText, StyledReadOnlyLabel } from '../../../styles';
import type { HighlightDropdownEventPayload } from '../events';
import { HIGHLIGHT_DROPDOWN_EVENT_KEY } from '../events';
import {
  BEST_EFFORT_PARSING_DEFAULT_STRING,
  BEST_EFFORT_PARSING_PLACEHOLDER_MULTISELECT,
  BEST_EFFORT_PARSING_PLACEHOLDER_STRING,
  isThresholdUsingNullOperator,
} from '~/dashboard/views/workflows/service/thresholds';
import { getIsCaretPositionInInsertionMode } from '../../useCaretControl/helpers';
import { getFirstEligibleActionForDynamicComparison } from 'flow/store/engine/update/thresholds';
import {
  invalidLeftSideDynamicComparisonTypes,
  minMaxLimitRequiredActionTypes,
  parenthesisDropdownModes,
  supportedNullActionTypes,
  validRangeDropdownModes,
  validRightSideDynamicComparisonModes,
  validRightSideStaticComparisonModes,
} from './constants';
import {
  booleanOptions,
  dynamicComparisonOptions,
  flagSetOptions,
  groupingOptions,
  logicOperatorOptions,
  noReasonCodeGroupOption,
  reasonCodeGroupOption,
} from '../../NodeEditorDrawer/constants';
import { UserContext } from '~/dashboard/context';
import { closeSearchBoxThunk } from 'flow/store/redux/thunks';
import { useEditorAccess } from 'flow/editor/context';
import { getIsEditable } from 'flow/service/general';
import { getDollarCurrencyRightAttributeValueMode } from '../helpers';

function RuleThresholdDropdown({
  parentRulePath,
  andOrPath,
  mode,
  insertion,
  editable = true,
  inAmbiguousScope,
}: {
  parentRulePath?: string;
  andOrPath?: string;
  mode: RuleEditorDropdownMode;
  insertion?: boolean;
  editable?: boolean;
  inAmbiguousScope?: boolean;
}) {
  const dispatch = useAppDispatch();
  const addSnackBar = useSnackBar();

  const { actions } = useAppSelector(selectWorkflowData);
  const { services, reasonCodeGroups, workflow } = useAppSelector(selectWorkflowProps);
  const operationDescriptions = useAppSelector(selectWorkflowOperationDescs);
  const nodeLineage = useAppSelector(selectNodeLineageForAnchorNode);
  const isSearchBoxVisible = useAppSelector(selectIsSearchBoxVisible);
  const inputAttributeNodeFolder = useAppSelector(selectNodeFolderState);

  const { featureFlags } = useContext(UserContext);
  const supportsBestEffortParsing = featureFlags.allowBestEffortRuleEvaluations;

  const multiSelectRef = useRef(null);
  const isInputPreviouslyBlurred = React.useRef(false);

  const {
    threshold,
    thresholdType,
    leftDataType,
  } = useContext(ThresholdContext);

  const {
    updateRuleItemRefs,
    morphingCaretPosition,
    setMorphingCaretPosition,
    getFlattenedCaretPositionIndex,
    sourceAction,
    setPendingMorphingCaretPositionUpdateValue,
    activeInputValue,
    setActiveInputValue,
    getNextCaretPosition,
    pendingGroupOpeningParenthesisInfo,
    setPendingGroupOpeningParenthesisInfo,
    simpleEditorMode,
    setIsolatedEditingBlock,
    isolatedEditingBlock,
    containsAmbiguousRuleOperator,
    ruleEditorPanelBoxContainerWidth,
    isMatrixModelFocused,
    ActionHoverTooltip,
  } = useContext(RuleEditorContext);

  const [isDatePickerOpen, setDatePickerOpen] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(false);

  const { editorMode } = useEditorAccess();
  const inEditMode = getIsEditable(editorMode);

  useEffect(() => {
    const isParenthesisDropdown = parenthesisDropdownModes.has(mode);
    const listenHighlightEvent = (e: CustomEvent<HighlightDropdownEventPayload>) => {
      const { modeKey, path } = e.detail.index;
      if (path === parentRulePath && modeKey === mode) setIsHighlighted(e.detail.isHighlighted);
    };

    if (isParenthesisDropdown) {
      document.addEventListener(HIGHLIGHT_DROPDOWN_EVENT_KEY, listenHighlightEvent);
    }
    return () => {
      if (isParenthesisDropdown) {
        document.removeEventListener(HIGHLIGHT_DROPDOWN_EVENT_KEY, listenHighlightEvent);
      }
    };
  }, [activeInputValue.path, mode, parentRulePath]);

  // flattened path meaning we convert expression path (e.g. [0][1]..) into 0-1 - which is the format we use for caret position / caret indices
  const flattenedPathInCaretPositionFormat = useMemo(() => (getFlattenedCaretPositionIndex({
    itemPath: parentRulePath,
    mode,
    insertion,
    type: CaretIndexType.RULES,
  })), [getFlattenedCaretPositionIndex, parentRulePath, mode, insertion]);

  useEffect(() => {
    if (multiSelectRef?.current && flattenedPathInCaretPositionFormat) {
      updateRuleItemRefs({
        path: flattenedPathInCaretPositionFormat,
        width: multiSelectRef.current.controlRef?.offsetWidth,
      });
    }
  }, [flattenedPathInCaretPositionFormat, multiSelectRef, updateRuleItemRefs]);

  const stringMultiSelectOptions = useMemo(() => {
    if (threshold && mode === RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_MULTISELECT) {
      const attributeValues = threshold.left.attribute?.values
        ? threshold.left.attribute?.values.split(',')
        : [];

      return attributeValues;
    }
    return [];
  }, [mode, threshold]);

  const minMaxValueCap = useMemo(() => {
    if (!threshold || !threshold.left.attribute || leftDataType === 'string') return null;

    if (minMaxLimitRequiredActionTypes.has(threshold.left.action.type)) {
      if (threshold.left.action.type === ActionType.MATRIX_MODEL) {
        const { matrixDataType } = threshold.left.action.matrixModel;
        if (matrixDataType === 'string') return null;
        return {
          min: -50000000,
          max: 50000000,
        };
      }
      if (['decimal', 'integer'].includes(leftDataType)) {
        return {
          min: -50000000,
          max: 50000000,
        };
      }
    }

    const leftAttributeValues = threshold?.left.attribute.values;
    const minMaxValuesArray = leftAttributeValues ? leftAttributeValues.split(',') : [];

    if (minMaxValuesArray.length === 2) {
      return {
        min: Number(minMaxValuesArray[0]),
        max: Number(minMaxValuesArray[1]),
      };
    }

    return {
      min: DEFAULT_INTEGER_MIN,
      max: DEFAULT_INTEGER_MAX,
    };
  }, [leftDataType, threshold]);

  const dollarCurrencyRightAttributeValueMode = useMemo(() => (
    getDollarCurrencyRightAttributeValueMode(threshold?.left)
  ), [threshold?.left]);

  const regexRightAttributeValueMode = (
    mode === RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE
    && leftDataType !== 'reasoncodes'
    && ['in', 'nin'].includes(threshold.operator));

  const isNonEmptyServiceAction = useCallback(actionObject => {
    if (isActionOfType(actionObject, ActionType.SERVICE)) {
      const availableAttributesOfAction = getAllowedAttributesWithMatchingDataType({
        service: services[actionObject.service.internalName],
        incomingDataType: leftDataType,
        workflowType: workflow.type,
      });

      if (Object.values(availableAttributesOfAction).length === 0) {
        return false;
      }
    }
    return true;
  }, [services, leftDataType, workflow.type]);

  // this checks for if user is editing this dropdown, and removed all chars (empty), attr data type is string, and this dropdown is right attr value.
  // if this memo val is set to true, we use it to help user set "" (empty string) as the value.
  const isEditingRightValueAndDeletingAllChars = (
    activeInputValue.path === parentRulePath
    && activeInputValue.value?.length === 0
    && morphingCaretPosition === flattenedPathInCaretPositionFormat
    && leftDataType === 'string'
    && mode === RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE
    && threshold.right.attributeValue !== BEST_EFFORT_PARSING_DEFAULT_STRING
    && String(threshold.right.attributeValue)?.length >= 0
  );

  const options = React.useMemo<DropdownOption[]>(() => {
    const lengthLimit = computeRuleItemLengthLimit(ruleEditorPanelBoxContainerWidth);
    // the dropdown is for configuring svc action attribute
    const getThresholdAttributeOptions = (key: 'left' | 'right'): DropdownOption[] => {
      /*
        Temporarily hiding features for Portfolio Evaluations
        Epic ID: 109173
      */
      const allowedAttributes: {
        [attribute_id: number]: Attribute;
      } = getAllowedAttributesForPortfolioWorkflow(
        workflow.type,
        services[threshold[key].service.internal_name]?.name,
        threshold[key].service.attributes,
      );

      const availableAllAttributesOfSelectedAction = Object.entries(
        allowedAttributes,
      ).sort((a, b) => a[1].display_name.localeCompare(b[1].display_name))
        .filter(([, attribute]) => {
          let rightSideAttributeAvailability = true; // when it's left side, just default it to true
          if (key === 'right') {
            const dataTypeNotMatchingLeftAttrDataType = attribute.dataType !== leftDataType;
            if (dataTypeNotMatchingLeftAttrDataType
              || !canDynamicallyCompareActions(threshold.left.action, threshold.right.action)) {
              rightSideAttributeAvailability = false;
            }
          }
          // sometimes this happens and it's wrong attributes
          const mlaWithoutComponents = attribute.is_multilevel && !attribute.components;
          return !attribute.archivedAt && rightSideAttributeAvailability
            && !mlaWithoutComponents;
        })
        .map(([id, attribute]): DropdownOption => ({
          label: attribute.display_name,
          value: id,
          meta: {
            isMLA: attribute.is_multilevel,
          },
        }));

      // if not MLA, return attributes now
      if (!checkIfServiceHasMLAAttributes(threshold[key].service.internal_name)) {
        return availableAllAttributesOfSelectedAction;
      }
      // otherwise, use MLA specific dropdown options
      const otherAttributes = availableAllAttributesOfSelectedAction.filter(
        attribute => attribute.value !== String(threshold[key].attributeId));
      const mlaOptionArray: DropdownOption[] = [
        {
          label: 'Other Multi-Level Attributes',
          value: undefined,
          options: otherAttributes.filter(
            attribute => attribute.meta.isMLA,
          ),
        },
        {
          label: 'All Other Attributes',
          value: undefined,
          options: availableAllAttributesOfSelectedAction.filter(
            attribute => !attribute.meta.isMLA,
          ),
        },
      ];

      const isAttributeNotArchived = !threshold[key].attribute.archivedAt
        || threshold[key].attribute.components?.length;
      const isCurrentAttrMLA = threshold[key].attribute.isMultilevel;
      return !isCurrentAttrMLA ? mlaOptionArray
        : [
          isAttributeNotArchived && {
            label: 'Edit Attribute',
            value: String(threshold[key].attributeId),
            meta: {
              shortenedLabel: threshold[key].attribute.display_name,
            },
          },
          ...mlaOptionArray,
        ];
    };

    const getThresholdAttributeMinOrMaxOptionArray = (
      thresholdValueIndex: 0 | 1,
    ): DropdownOption[] => {
      const rightAttrValueString = String(threshold.right.attributeValue);
      const thresholdValues = rightAttrValueString.split(','); // shape: [min, max]
      const valueForArray = thresholdValues[thresholdValueIndex];

      if (!dollarCurrencyRightAttributeValueMode) {
        return [{ value: valueForArray, label: valueForArray }];
      }

      const amount = parseFloat(valueForArray);
      const dollarAmount = `${(amount / 100).toFixed(2)}`;
      return [{
        value: rightAttrValueString,
        label: dollarAmount, // MLA dollar integer value should be formatted like this
        meta: {
          shortenedLabel: dollarAmount,
        },
      }];
    };

    const getThresholdRightAttributeValueOptionArray = (): DropdownOption[] => {
      if (threshold.right.attributeValue === null && !regexRightAttributeValueMode) {
        return [{ label: '', value: '' }];
      }
      if (threshold.left.action.type === ActionType.OUTCOME_REASON) {
        return flagSetOptions;
      }
      const rightAttrValueString = String(threshold.right.attributeValue);

      if (dollarCurrencyRightAttributeValueMode) {
        const amount = parseFloat(rightAttrValueString);
        const dollarAmount = `$${(amount / 100).toFixed(2)}`;
        return [{
          value: rightAttrValueString,
          label: dollarAmount, // MLA dollar integer value should be formatted like this
          meta: {
            shortenedLabel: dollarAmount,
          },
        }];
      }

      if (regexRightAttributeValueMode) {
        // When right attr value = null then rightAttrValueString = "null"
        const displayRegexString = threshold.right.attributeValue ? rightAttrValueString : '';
        return [{
          icon: <Icon icon={IconName.Edit} />,
          label: 'Edit Regex',
          value: displayRegexString,
          meta: {
            hideIcon: true,
            shortenedLabel: truncateLabel(displayRegexString, lengthLimit),
          },
        }];
      }

      return [{
        value: rightAttrValueString,
        label: rightAttrValueString,
        meta: {
          shortenedLabel: truncateLabel(rightAttrValueString, lengthLimit),
        },
      }];
    };

    const getGroupStartOptions = isGroupStartPendingMode => {
      // the dropdown is for configuring group open parenthesis
      if (!pendingGroupOpeningParenthesisInfo.path) {
        return isGroupStartPendingMode
          ? [groupingOptions.open]
          : [groupingOptions.start, groupingOptions.ungroup];
      }
      if (pendingGroupOpeningParenthesisInfo.path === parentRulePath && insertion) {
        return [groupingOptions.start, groupingOptions.cancel];
      }
      return [groupingOptions.start, groupingOptions.ungroup];
    };
    // dropdown options
    // we wrap expensive value calls in a function to lazily load them in
    const optionConfig = {
      [RuleEditorDropdownMode.RULE_OPERATOR_AND]: logicOperatorOptions,
      [RuleEditorDropdownMode.RULE_OPERATOR_OR]: logicOperatorOptions,
      [RuleEditorDropdownMode.THRESHOLD_OPERATOR]:
        () => getOperatorsAsOptions({
          attribute: threshold.left.attribute,
          dataType: leftDataType,
          thresholdType,
          threshold,
          supportsBestEffortParsing,
        }),
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_MULTISELECT]:
        () => stringMultiSelectOptions.map(value => ({ label: value, value })),
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_BOOLEAN]: booleanOptions,
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_REASONCODEGROUP]:
        () => {
          const reasonCodeItems = reasonCodeGroups
            .filter(reasonCodeGroup => !reasonCodeGroup.archived)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(({ name, id }) => ({ label: name, value: String(id) }));

          if (reasonCodeItems.length === 0) {
            return [noReasonCodeGroupOption];
          }
          return reasonCodeItems;
        },
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_TAGACTIONRESULT]: flagSetOptions,
      [RuleEditorDropdownMode.THRESHOLD_LEFT_ACTION]:
        () => getNonCyclicalActions({
          actions, lengthLimit, sourceAction, nodeLineage, services,
        }),
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ACTION]:
        // Exclude matrix models from RHS dynamic comparison UI
        () => getNonCyclicalActions({
          actions, lengthLimit, sourceAction, nodeLineage, services, excludeMatrixModels: true,
        }).filter(({ value }) => (
          isNonEmptyServiceAction(actions[value])
          && canDynamicallyCompareActions(threshold.left.action, actions[value]))),
      [RuleEditorDropdownMode.CARET_THRESHOLD]: !threshold
        // new insertion mode; it'll always be left action selection first.
        // for rule operator, we automatically follow whatever the rule operator in this scope, hence user doesn't get that option here
        ? () => getNonCyclicalActions({
          actions, lengthLimit, sourceAction, nodeLineage, services,
        })
        : [],
      [RuleEditorDropdownMode.GROUP_START]:
        () => getGroupStartOptions(false),
      [RuleEditorDropdownMode.GROUP_START_PENDING]:
        () => getGroupStartOptions(true),
      [RuleEditorDropdownMode.GROUP_END]: () => (
        (
          pendingGroupOpeningParenthesisInfo.path
          && getIsCaretPositionInInsertionMode(morphingCaretPosition)
          && insertion
        ) ? [groupingOptions.close, groupingOptions.cancel]
          : [groupingOptions.end, groupingOptions.ungroup]
      ),
      [RuleEditorDropdownMode.THRESHOLD_LEFT_ATTRIBUTE]:
        () => getThresholdAttributeOptions('left'),
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE]:
        () => getThresholdAttributeOptions('right'),
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_RANGE_MAX]:
        () => getThresholdAttributeMinOrMaxOptionArray(1),
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_RANGE_MIN]:
        () => getThresholdAttributeMinOrMaxOptionArray(0),
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE]:
        () => getThresholdRightAttributeValueOptionArray(),
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_DATEPICKER]:
        () => {
          const formattedDateTime = formatDateTime(
            String(threshold.right.attributeValue),
            leftDataType,
          );
          return [{
            icon: <Icon icon={IconName.Calendar} />,
            label: `Edit ${capitalize(leftDataType)}`,
            value: formattedDateTime,
            meta: {
              hideIcon: true,
              shortenedLabel: truncateLabel(formattedDateTime, lengthLimit),
            },
          }];
        },
    };

    // lazy load in a value that is setup to be lazy loaded
    const safelyGetValue = (val: DropdownOption[] | (() => DropdownOption[])) => (
      typeof val === 'function' ? val() : val
    );

    if (validRangeDropdownModes.has(mode) && minMaxValueCap) {
      const isCapDefaultIntegerValue = minMaxValueCap.min === DEFAULT_INTEGER_MIN
        && minMaxValueCap.max === DEFAULT_INTEGER_MAX;
      const defaultPromptForValueCap = `Enter ${leftDataType === 'decimal' ? 'a ' : 'an '}${leftDataType}`;
      const messageForValueCap = isCapDefaultIntegerValue
        ? defaultPromptForValueCap
        : `${defaultPromptForValueCap} between ${minMaxValueCap.min} and ${minMaxValueCap.max}`;

      return [{
        label: messageForValueCap,
        value: undefined,
        options: safelyGetValue(optionConfig[mode]),
      }];
    }

    const config = optionConfig[mode];
    if (!config) { return []; } // fail with grace

    if (isEditingRightValueAndDeletingAllChars && !regexRightAttributeValueMode) {
      return [{ label: '', value: '' }];
    }

    return safelyGetValue(config);
  }, [
    actions, mode, threshold, sourceAction,
    pendingGroupOpeningParenthesisInfo, morphingCaretPosition,
    stringMultiSelectOptions, parentRulePath, leftDataType, thresholdType,
    reasonCodeGroups, nodeLineage, minMaxValueCap, services, workflow,
    insertion, ruleEditorPanelBoxContainerWidth, regexRightAttributeValueMode,
    dollarCurrencyRightAttributeValueMode, supportsBestEffortParsing,
    isNonEmptyServiceAction, isEditingRightValueAndDeletingAllChars,
  ]);

  const firstEligibleActionForDynamicComparison = useMemo(
    () => {
      if (!threshold) return null;
      const actionIdsConnectedDownstreamFromThresholdOwner = new Set<string>([
        ...nodeLineage.directOutgoers,
        ...nodeLineage.extendedOutgoers,
        threshold.left.actionId,
      ]);

      return getFirstEligibleActionForDynamicComparison({
        threshold,
        actions,
        workflow,
        services,
        actionIdsConnectedDownstreamFromThresholdOwner,
      });
    }, [threshold, actions, workflow, services, nodeLineage]);

  const ctaOptionsForRightSide = useMemo(() => {
    const ctaOptions = [];
    if (
      validRightSideDynamicComparisonModes.has(mode)
      && !invalidLeftSideDynamicComparisonTypes.has(threshold.left.action.type)
      && firstEligibleActionForDynamicComparison
    ) {
      ctaOptions.push({
        icon: <Icon icon={IconName.Rerun} size="small" />,
        ...dynamicComparisonOptions.toDynamic,
      });
    } else if (validRightSideStaticComparisonModes.has(mode)) {
      ctaOptions.push({
        icon: <Icon icon={IconName.Rerun} size="small" />,
        ...dynamicComparisonOptions.toStatic,
      });
    }
    if (mode === RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_REASONCODEGROUP) {
      ctaOptions.push({
        icon: <Icon color="blue" icon={IconName.ExternalService} size="small" />,
        ...reasonCodeGroupOption,
      });
    }

    return ctaOptions.length ? ctaOptions : null;
  }, [mode, threshold, firstEligibleActionForDynamicComparison]);

  const thresholdOperatorIsNullOrNotNull = (
    mode === RuleEditorDropdownMode.THRESHOLD_OPERATOR
      && thresholdType !== 'dynamic'
      && isThresholdUsingNullOperator({
        leftDataType,
        threshold,
        supportsBestEffortParsing,
      })
  );

  const shouldThresholdRightValueUseDefaultString = (
    supportsBestEffortParsing
    && leftDataType === 'string'
    && supportedNullActionTypes.has(threshold.left.action.type)
    && ['e', 'ne'].includes(threshold.operator)
    && (
      mode === RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE
      || mode === RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_MULTISELECT
    )
  );

  const selectedOption = useMemo(() => {
    if (isEditingRightValueAndDeletingAllChars && !regexRightAttributeValueMode) return [];

    const incomingDropdownValue = String(getSelectedOptionValue({
      mode,
      threshold,
      leftDataType,
    }));

    if (mode === RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_MULTISELECT) {
      return options.filter(({ value }) => incomingDropdownValue.split(',').find(
        attributeValue => attributeValue === value,
      ));
    }

    if (thresholdOperatorIsNullOrNotNull) {
      return options.filter(({ value }) => value === `${incomingDropdownValue}_null`);
    }

    if (validRangeDropdownModes.has(mode) && minMaxValueCap) {
      return {
        value: String(incomingDropdownValue),
        label: dollarCurrencyRightAttributeValueMode
          ? `$${(parseFloat(incomingDropdownValue) / 100).toFixed(2)}`
          : String(incomingDropdownValue),
      };
    }

    return incomingDropdownValue && options?.length
      ? getSelectedOption({
        incomingValue: String(incomingDropdownValue),
        options,
      })
      : [];
  }, [leftDataType, mode, threshold, thresholdOperatorIsNullOrNotNull,
    minMaxValueCap, options, dollarCurrencyRightAttributeValueMode,
    isEditingRightValueAndDeletingAllChars, regexRightAttributeValueMode]);

  const tooltipContent = useMemo(() => {
    if (ActionHoverTooltip && [
      RuleEditorDropdownMode.THRESHOLD_LEFT_ACTION,
      RuleEditorDropdownMode.THRESHOLD_RIGHT_ACTION,
    ].includes(mode) && !Array.isArray(selectedOption) && selectedOption?.value) {
      const hoveredAction = actions[selectedOption.value];
      return (
        <ActionHoverTooltip action={hoveredAction} />
      );
    }

    let customMessage = '';

    if (thresholdOperatorIsNullOrNotNull && leftDataType === 'string') {
      customMessage = 'This refers to empty string, null, or undefined.';
    } else {
      customMessage = (
        !Array.isArray(selectedOption) && selectedOption?.label
          ? selectedOption?.label
          : ''
      );
    }

    return customMessage.length ? (
      <StyledCodeStyleText>
        {customMessage}
      </StyledCodeStyleText>
    ) : null;
  }, [
    ActionHoverTooltip, actions, leftDataType, mode,
    selectedOption, thresholdOperatorIsNullOrNotNull]);

  const isSwitchingToDatePicker = isDatePickerOpen && !Array.isArray(selectedOption);

  const onChangeResetToMinMaxLimit = useCallback(newValue => {
    if (newValue < minMaxValueCap.min) {
      addSnackBar({
        title: 'The value was reset to the minimum value allowed for this filter.',
        type: 'warning',
      });
      return minMaxValueCap.min;
    }

    if (newValue > minMaxValueCap.max) {
      addSnackBar({
        title: 'The value was reset to the maximum value allowed for this filter.',
        type: 'warning',
      });
      return minMaxValueCap.max;
    }

    return newValue;
  }, [addSnackBar, minMaxValueCap]);

  // helper function for onChange
  const getThresholdRightAttributeValue = useCallback(
    (value: any, inputValue: any) => {
      let newValue = value;
      if (['decimal', 'integer'].includes(leftDataType)) {
        newValue = Number.parseFloat(value);
        if (leftDataType === 'integer') {
          newValue = newValue.toFixed(0);
        }
        if (isNaN(newValue)) {
          newValue = 0;
        }
        if (minMaxValueCap) {
          newValue = onChangeResetToMinMaxLimit(newValue);
        }
      } else if (shouldThresholdRightValueUseDefaultString && !newValue) {
        newValue = BEST_EFFORT_PARSING_DEFAULT_STRING;
      } else if (threshold.left.action.type === ActionType.OUTCOME_REASON) {
        newValue = inputValue.value;
      } else {
        newValue = inputValue.label; // react select returns string value with all space truncation and lowercase casting
      }

      if (dollarCurrencyRightAttributeValueMode) {
        let amount = parseFloat(inputValue.label.replace(/\$/g, ''));
        amount = parseFloat(amount.toFixed(2));
        newValue = Math.round(amount * 100); // MLA dollar integer value should be formatted like this
      }
      return newValue;
    }, [dollarCurrencyRightAttributeValueMode, threshold, leftDataType, minMaxValueCap,
      onChangeResetToMinMaxLimit, shouldThresholdRightValueUseDefaultString]);

  // helper function for onChange
  const getThresholdRightAttributeValueRangeValue = useCallback(
    (value: any, inputValue) => {
      let newValue;
      const originalAttrValue = String(threshold.right.attributeValue).split(',');

      if (dollarCurrencyRightAttributeValueMode) {
        let amount = parseFloat(inputValue.label.replace(/\$/g, ''));
        amount = parseFloat(amount.toFixed(2));
        amount = Math.round(amount * 100);
        if (mode === RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_RANGE_MAX) {
          newValue = [originalAttrValue[0], amount].join(','); // MLA dollar integer value should be formatted like this
        } else {
          newValue = [amount, originalAttrValue[1]].join(',');
        }
      } else {
        newValue = mode === RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_RANGE_MAX
          ? [originalAttrValue[0], value].join(',')
          : [value, originalAttrValue[1]].join(',');
      }

      if (minMaxValueCap) {
        newValue = onChangeResetToMinMaxLimit(newValue);
      }
      return newValue;
    }, [dollarCurrencyRightAttributeValueMode, minMaxValueCap, mode,
      onChangeResetToMinMaxLimit, threshold?.right?.attributeValue]);

  const onChange = useCallback(inputValue => {
    let value;
    if (stringMultiSelectOptions.length <= 0) {
      value = inputValue.value;
    }
    const thresholdId = threshold?.publicId;
    if (!Array.isArray(inputValue)) {
      if (inputValue.value === 'configure_reason_code_groups') {
        window.open(routes.v3.dashboard.settings.reason_codes, '_blank').focus();
        return undefined;
      }

      if (inputValue.value === 'switch_rightside_to_dynamic') {
        // user attempts dynamic comparison
        // this value can only come from threshold right attribute side dropdown modes
        return dispatch(switchThresholdToDynamicComparison({
          thresholdId, isNodeFolderCollapsed: inputAttributeNodeFolder,
        }));
      }

      if (inputValue.value === 'switch_rightside_to_static') {
        return dispatch(switchThresholdToStaticComparison({ thresholdId }));
      }
    }

    if (inputValue.label === 'Edit Regex' && regexRightAttributeValueMode) {
      return setIsolatedEditingBlock({
        originPublicId: threshold.publicId,
        originType: ThresholdRightAttributeOrigin.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_REGEX,
        originCaretIndex: flattenedPathInCaretPositionFormat,
      });
    }

    if (
      mode === RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_DATEPICKER
      && inputValue.label.includes('Edit')
    ) {
      return setDatePickerOpen(true);
    }
    // simple RuleEditor cases where we only dispatch updateThreshold
    // we wrap expensive value calls in a function to lazily load them
    const simpleDispatchCases = {
      [RuleEditorDropdownMode.THRESHOLD_LEFT_ACTION]: { path: `${thresholdId}.left.actionId`, value },
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ACTION]: { path: `${thresholdId}.right.actionId`, value },
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_REASONCODEGROUP]: {
        path: `${thresholdId}.right.reasonCodeGroupId`,
        value: +value,
      },
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_BOOLEAN]: {
        path: `${thresholdId}.right.attributeValue`,
        value: String(value),
      },
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_MULTISELECT]: {
        path: `${thresholdId}.right.attributeValue`,
        value: () => {
          let stringValue = inputValue.map(({ value: multiSelectValue }) => multiSelectValue).join(',');
          if (supportsBestEffortParsing && !stringValue) stringValue = null;
          return stringValue;
        },
      },
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_TAGACTIONRESULT]: {
        path: `${thresholdId}.right.leftTagActionResult`,
        value: () => JSON.parse(value),
      },
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE]: {
        path: `${thresholdId}.right.attributeValue`,
        value: () => getThresholdRightAttributeValue(value, inputValue),
      },
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_DATEPICKER]: {
        path: `${thresholdId}.right.attributeValue`,
        value: String(value),
      },
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_RANGE_MAX]: {
        path: `${thresholdId}.right.attributeValue`,
        value: () => getThresholdRightAttributeValueRangeValue(value, inputValue),
      },
      [RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_RANGE_MIN]: {
        path: `${thresholdId}.right.attributeValue`,
        value: () => getThresholdRightAttributeValueRangeValue(value, inputValue),
      },
    };

    if (simpleDispatchCases[mode]) {
      const dispatchArgs = simpleDispatchCases[mode];
      dispatch(updateThreshold({
        path: dispatchArgs.path,
        // lazy load in potentially expensive op
        value: typeof dispatchArgs.value === 'function' ? dispatchArgs.value() : dispatchArgs.value,
      }));
    }

    // the rest of the cases require more than just dispatching threshold updates
    switch (mode) {
      case RuleEditorDropdownMode.THRESHOLD_LEFT_ATTRIBUTE:
      case RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE: {
        const key = mode === RuleEditorDropdownMode.THRESHOLD_LEFT_ATTRIBUTE
          ? 'left'
          : 'right';
        if (threshold[key].attribute?.isMultilevel
          && inputValue.label === 'Edit Attribute') {
          setIsolatedEditingBlock({
            originPublicId: thresholdId,
            originType: mode === RuleEditorDropdownMode.THRESHOLD_LEFT_ATTRIBUTE
              ? MLAAttributeItemOrigin.THRESHOLD_LEFT
              : MLAAttributeItemOrigin.THRESHOLD_RIGHT,
          });
        } else {
          dispatch(updateThreshold({
            path: `${thresholdId}.${key}.attributeId`, value: Number(value),
          }));
        }
        break;
      }
      case RuleEditorDropdownMode.THRESHOLD_OPERATOR: {
        if (value === 'range') {
          dispatch(updateThreshold({
            path: `${thresholdId}.right.attributeValue`, value: `${threshold.right.attributeValue},0`,
          }));
        }
        const isMatchingRegexOperator = leftDataType === 'string' && ['in', 'nin'].includes(threshold.operator);
        if (isMatchingRegexOperator) {
          dispatch(updateThreshold({ path: `${thresholdId}.right.attributeValue`, value: '' }));
        }
        if (value.startsWith('codes_')) { // we remove this prefix because it's solely used for dropdowns
          value = value.replace('codes_', '');
        }

        // handling is null/not null operator option:
        if (['e_null', 'ne_null'].includes(value)) {
          // string null value = '' while integer/decimal null value = null
          const nullValue = leftDataType === 'string' ? '' : null;
          dispatch(updateThreshold({ path: `${thresholdId}.right.attributeValue`, value: nullValue }));
        } else if (thresholdOperatorIsNullOrNotNull && leftDataType !== 'string') {
          // for resetting integer or decimal data types back to "= 0.0"
          dispatch(updateThreshold({ path: `${thresholdId}.right.attributeValue`, value: 0.0 }));
        } else if (thresholdOperatorIsNullOrNotNull && leftDataType === 'string') {
          // for resetting string data type back to "= enter_string"
          dispatch(updateThreshold({
            path: `${thresholdId}.right.attributeValue`, value: BEST_EFFORT_PARSING_DEFAULT_STRING,
          }));
        }

        dispatch(updateThreshold({
          path: `${thresholdId}.operator`, value: inputValue.meta?.customKey || value,
        }));
        break;
      }
      case RuleEditorDropdownMode.GROUP_END:
      case RuleEditorDropdownMode.GROUP_START:
      case RuleEditorDropdownMode.GROUP_START_PENDING: {
        if (value === 'cancel_pending_group') {
          setPendingGroupOpeningParenthesisInfo({ path: null });
        } else if (value === 'ungroup') {
          dispatch(updateActionRulesGrouping({
            id: sourceAction.publicId,
            groupParenthesisPath: parentRulePath,
          }));
        } else if ([
          RuleEditorDropdownMode.GROUP_START,
          RuleEditorDropdownMode.GROUP_START_PENDING,
        ].includes(mode)) {
          setPendingGroupOpeningParenthesisInfo({ path: parentRulePath });
        } else if (mode === RuleEditorDropdownMode.GROUP_END) {
          if (value === ')') {
            dispatch(updateActionRulesGrouping({
              id: sourceAction.publicId,
              openingParenthesisPath: pendingGroupOpeningParenthesisInfo.path,
              closingParenthesisPath: parentRulePath,
            }));
            setPendingGroupOpeningParenthesisInfo({ path: null });
          }
        }
        break;
      }
      case RuleEditorDropdownMode.RULE_OPERATOR_AND:
      case RuleEditorDropdownMode.RULE_OPERATOR_OR: {
        if ((value === 'and' || value === 'or')
          // only dispatch update if the rule operator is actually changing:
          && (!Array.isArray(selectedOption) && selectedOption?.value !== value)
        ) {
          dispatch(updateActionRulesOperator({
            id: sourceAction.publicId,
            newRuleOperator: value,
            rulePathToPerformActionAt: andOrPath,
          }));
        }
        break;
      }
    }

    const lastAssistanceMessageToDismiss = findLastOperation({
      operations: operationDescriptions, publicId: sourceAction.publicId,
    });
    if (lastAssistanceMessageToDismiss) {
      dispatch(dismissWorkflowOperationMessage({ id: lastAssistanceMessageToDismiss.id }));
    }

    // we update caret position accordingly because user changed something, and expect the cursor to be moving to the next.
    // so we store the index of where the cursor should be to pending update state in the context and let it update once after indices are regenerated.
    if (!inputValue?.fromBlurEvent) {
      if (!isInputPreviouslyBlurred.current) {
        // checking for "!isInputPreviouslyBlurred.current" due to
        // when preforming key down 'enter', react-select runs both
        // onBlur and onCreateOption (with no blur event)
        setActiveInputValue({
          value: '',
          path: '',
          initialValue: false,
        });
      }

      if (!mode.includes('ruleOperator')) {
        const { newPositionString, newPositionIndexForDelayedUpdate } = getNextCaretPosition({
          userActionObject: {
            meta: undefined, // @todo: this is temp, eventually going away
            value,
            mode,
          },
        });

        if (newPositionIndexForDelayedUpdate) {
          setPendingMorphingCaretPositionUpdateValue(newPositionIndexForDelayedUpdate);
        } else {
          setMorphingCaretPosition(newPositionString);
        }
      }
    } else {
      setMorphingCaretPosition('');
    }

    return null;
  }, [
    setActiveInputValue, leftDataType, selectedOption,
    getThresholdRightAttributeValue, getThresholdRightAttributeValueRangeValue,
    stringMultiSelectOptions, threshold, mode, getNextCaretPosition, dispatch,
    setPendingGroupOpeningParenthesisInfo, setIsolatedEditingBlock, sourceAction,
    parentRulePath, regexRightAttributeValueMode, flattenedPathInCaretPositionFormat,
    operationDescriptions, andOrPath, setPendingMorphingCaretPositionUpdateValue,
    setMorphingCaretPosition, pendingGroupOpeningParenthesisInfo,
    thresholdOperatorIsNullOrNotNull, supportsBestEffortParsing,
  ]);

  const selectProps = getSelectProps(
    !!ctaOptionsForRightSide,
    mode,
    regexRightAttributeValueMode,
    simpleEditorMode,
    threshold,
  );

  const updateValueUsingActiveInputValue = (inputValue: string) => {
    if (
      (inputValue.length > 0 && validRangeDropdownModes.has(mode))
      || isEditingRightValueAndDeletingAllChars
    ) {
      onChange({
        label: inputValue,
        value: inputValue,
        textInput: true,
        fromBlurEvent: true,
      });
      setActiveInputValue({
        value: '',
        path: '',
        initialValue: false,
      });
    }
  };

  const onInputChange = (inputValue: string, { action }: InputActionMeta) => {
    // Need to check for react-select action payload otherwise we are
    // updating active input value even on key down actions like enter
    if (action === 'input-change') {
      // we track this to handle dropdown delete key down handling
      setActiveInputValue({
        value: inputValue,
        path: parentRulePath,
        initialValue: false,
      });

      if (inputValue?.endsWith(' ')
        && (
          mode === RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE
          && leftDataType !== 'string'
        )) {
        // whitespace at the end, we'll try see if this is just number/operator with a whitespace at the end
        // if so, we'll automatically "submit" this for user convenience
        const trimmedInputValue = inputValue.slice(0, inputValue.length - 1);
        updateValueUsingActiveInputValue(trimmedInputValue);
      }
    }
  };

  const isDropdownCreatable = (
    validRangeDropdownModes.has(mode)
    && !regexRightAttributeValueMode
    && threshold.left.action.type !== ActionType.OUTCOME_REASON
  );

  const onBlur = e => {
    // This is needed to to avoid updating active input value
    // when switching over to render date picker
    if (isSwitchingToDatePicker) {
      return;
    }

    if (
      morphingCaretPosition.endsWith(`${CaretPositionSuffixTypes.GROUP_START}${CaretPositionSuffixTypes.CARET_INSERTION}`)
      || morphingCaretPosition.endsWith(`${CaretPositionSuffixTypes.GROUP_END}${CaretPositionSuffixTypes.CARET_INSERTION}`)
    ) {
      setMorphingCaretPosition('');
    } else if (isDropdownCreatable) {
      // if there's any value provided by user, we'll automatically "submit" this for user convenience
      updateValueUsingActiveInputValue(activeInputValue.value);
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
    isInputPreviouslyBlurred.current = true;
  };

  useEffect(() => {
    if (
      (
        morphingCaretPosition === flattenedPathInCaretPositionFormat
        || mode === RuleEditorDropdownMode.CARET_THRESHOLD
      )
      && !isolatedEditingBlock.originPublicId
      && !isMatrixModelFocused
      // NOTE: We don't want to have the action dropdown menu open if the user
      // has just started a new group because too many things popup on the screen
      && !(
        mode === RuleEditorDropdownMode.THRESHOLD_LEFT_ACTION
        && pendingGroupOpeningParenthesisInfo.path?.length
      )
    ) {
      // focus the dropdown automatically when caret position is pointing here
      multiSelectRef?.current?.focus();
    }
  }, [flattenedPathInCaretPositionFormat, isolatedEditingBlock,
    mode, isMatrixModelFocused, morphingCaretPosition, pendingGroupOpeningParenthesisInfo.path]);

  useEffect(() => {
    if (morphingCaretPosition === '') {
      // focus the dropdown automatically when caret position is pointing here
      multiSelectRef?.current?.blur();
    }
  }, [flattenedPathInCaretPositionFormat, multiSelectRef, morphingCaretPosition]);

  const inputValue = useMemo(() => {
    if (
      !Array.isArray(selectedOption)
      && isDropdownCreatable
      && activeInputValue.path === parentRulePath
      && morphingCaretPosition === flattenedPathInCaretPositionFormat
    ) {
      if (activeInputValue.value?.length === 0) return '';
      if (activeInputValue.value?.length > 0 || !activeInputValue.initialValue) {
        return activeInputValue.value;
      }

      return selectedOption?.value || '';
    }

    return undefined;
  }, [
    selectedOption,
    isDropdownCreatable,
    activeInputValue,
    parentRulePath,
    morphingCaretPosition,
    flattenedPathInCaretPositionFormat,
  ]);

  const onFocus = () => {
    if (isSearchBoxVisible) {
      dispatch(closeSearchBoxThunk());
    }
    if (morphingCaretPosition !== flattenedPathInCaretPositionFormat
      && flattenedPathInCaretPositionFormat) {
      // update caret position to be here if focused by other ways (mouse click etc)
      setMorphingCaretPosition(`${flattenedPathInCaretPositionFormat}`);
    }

    if (
      !Array.isArray(selectedOption)
      && isDropdownCreatable
    ) {
      setActiveInputValue({
        value: dollarCurrencyRightAttributeValueMode && selectedOption?.value
          ? `$${(parseFloat(selectedOption.value) / 100).toFixed(2)}`
          : inputValue || selectedOption?.value || '',
        path: parentRulePath,
        initialValue: true,
      });
    }
    isInputPreviouslyBlurred.current = false;
  };

  const onEscKey = (_: string, e: React.KeyboardEvent<HTMLInputElement>) => {
    onEscKeyFromRuleDropdown(
      e,
      activeInputValue,
      setActiveInputValue,
      multiSelectRef,
    );
  };

  const customFilterOptions = (
    options?.length <= 1
    && mode === RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE
    && !ctaOptionsForRightSide
    && !regexRightAttributeValueMode
  ) ? [] : options;

  const customPlaceholder = useMemo(() => {
    if (pendingGroupOpeningParenthesisInfo.path !== undefined
      && pendingGroupOpeningParenthesisInfo.path === parentRulePath
      && mode === RuleEditorDropdownMode.GROUP_START_PENDING
    ) {
      return '(';
    }

    if ([
      RuleEditorDropdownMode.CARET_THRESHOLD,
      RuleEditorDropdownMode.GROUP_START_PENDING,
    ].includes(mode)) {
      return '';
    }

    if (
      mode === RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_REASONCODEGROUP
      && options.length === 1
      && options[0].value === ''
    ) {
      return 'No Reason Code Groups';
    }

    if (regexRightAttributeValueMode
      && (!selectedOption || (Array.isArray(selectedOption) && !selectedOption.length))) {
      return 'edit regex...';
    }

    if (shouldThresholdRightValueUseDefaultString
      && mode === RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE
    ) {
      return BEST_EFFORT_PARSING_PLACEHOLDER_STRING;
    }
    if (shouldThresholdRightValueUseDefaultString
      && mode === RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_MULTISELECT
    ) {
      return BEST_EFFORT_PARSING_PLACEHOLDER_MULTISELECT;
    }

    return '...';
  }, [mode, pendingGroupOpeningParenthesisInfo, parentRulePath, regexRightAttributeValueMode,
    selectedOption, options, shouldThresholdRightValueUseDefaultString]);

  if (!editable) {
    return (
      <StyledReadOnlyLabel>
        {!Array.isArray(selectedOption)
          ? selectedOption?.label
          : ''}
      </StyledReadOnlyLabel>
    );
  }
  // @ts-ignore: 2339
  const selectedLabel = selectedOption?.shortenedLabel || selectedOption?.label || '';

  // when in the middle of opening a group, need to prevent other actions:
  const isDropdownDisabledDueToPendingGroup = !insertion
    && Boolean(pendingGroupOpeningParenthesisInfo.path);

  const isDropdownDisabled = Boolean(isolatedEditingBlock.originPublicId)
    || containsAmbiguousRuleOperator
    || isDropdownDisabledDueToPendingGroup
    || !inEditMode;

  const handleHighlight = (shouldHighlight: boolean) => {
    // prevent yellow highlight on disabled dropdowns unless to remove highlight
    if (isDropdownDisabled && shouldHighlight) return;

    setIsHighlighted(shouldHighlight);

    if (!parenthesisDropdownModes.has(mode)) return;

    // if moused over the open parenthesis, emit event to highlight the correlating
    // closed parenthesis. Vice versa for closed parenthesis
    const parenthesisModeToHighlight = mode === RuleEditorDropdownMode.GROUP_END
      ? RuleEditorDropdownMode.GROUP_START
      : RuleEditorDropdownMode.GROUP_END;

    document.dispatchEvent(new CustomEvent(
      HIGHLIGHT_DROPDOWN_EVENT_KEY,
      {
        detail: {
          index: {
            modeKey: parenthesisModeToHighlight,
            path: parentRulePath,
          },
          isHighlighted: shouldHighlight,
        },
      },
    ));
  };

  if (!options?.length && mode !== RuleEditorDropdownMode.GROUP_START_PENDING) return null;

  return (
    <Tooltip
      align="start"
      content={tooltipContent}
      side="top"
      variant="secondary"
      hide={(
        isDropdownDisabled
        || !tooltipContent
        || (
          selectedOption
          && !Array.isArray(selectedOption)
          && !selectedOption?.meta?.shortenedLabel
        )
        || !selectedOption
        || (Array.isArray(selectedOption) && !selectedOption.length)
      )}
    >
      <DropdownContainer
        highlighted={isHighlighted}
        onMouseOver={() => handleHighlight(true)}
        onMouseLeave={() => handleHighlight(false)}
        onBlur={() => handleHighlight(false)}
        validationIssue={inAmbiguousScope}
        // @ts-ignore: 2339
        data-testid={`ruleThresholdDropdown-${selectedLabel.slice(0, 20).replace(' ', '-').toLowerCase()}`}
        data-value={selectedLabel}
        isDisabled={isDropdownDisabled}
        pendingItem={mode === RuleEditorDropdownMode.GROUP_START_PENDING}
      >
        {!isDatePickerOpen && (
          <MultiSelect
            {...selectProps}
            openMenuOnFocus
            ctaOptions={ctaOptionsForRightSide}
            customMenuPortalTarget={document.querySelector('.dashboard-view')}
            onChange={onChange}
            options={customFilterOptions}
            inputValue={inputValue}
            onFocus={onFocus}
            onBlur={onBlur}
            isDisabled={isDropdownDisabled}
            selectedOptions={selectedOption}
            isCreatable={isDropdownCreatable}
            isMulti={mode === RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_MULTISELECT}
            placeholder={customPlaceholder}
            onCreateOption={input => onChange({ ...input, textInput: true })}
            onInputChange={onInputChange}
            noOptionsMessage={() => (isDropdownCreatable ? null : 'No options')}
            formatCreateLabel={newValue => (`Change the value to ${newValue}`)}
            onKeyDownHandlers={{
              Escape: onEscKey,
              Enter: onBlur,
            }}
            ref={multiSelectRef}
          />
        )}
        {isDatePickerOpen && (
          <DatePicker
            autoFocus
            open
            value={inputValue ? dayjs(inputValue) : null}
            showTime={leftDataType === 'timestamp'}
            onChange={(date: Dayjs) => {
              const dateFormat = leftDataType === 'timestamp'
                ? 'YYYY-MM-DDTHH:mm:ss[Z]'
                : 'YYYY-MM-DD';
              const formattedDate = date.format(dateFormat);
              onChange({
                label: formattedDate,
                value: formattedDate,
                textInput: false,
                fromBlurEvent: false,
              });
              setDatePickerOpen(false);
            }}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              onBlur(e);
              setDatePickerOpen(false);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                onBlur(e);
                setDatePickerOpen(false);
              }
            }}
          />
        )}
      </DropdownContainer>
    </Tooltip>
  );
}

export default RuleThresholdDropdown;
